/**
 * ActiveSleepTimerCard
 *
 * A reusable card component that displays active sleep timers.
 * Supports multiple parallel timers for multi-child households.
 * Shows elapsed time since baby fell asleep with a live counter.
 * Used on both Baby-Care page and Baby Dashboard.
 *
 * Features:
 * - Live timer display per sleeping baby
 * - Glass-effect card with oversized decorative Moon icon
 * - Single "Beenden" button opening multi-step bottom-sheet
 * - Step 1: Choose between cancel or woke up
 * - Step 2: Confirm cancel / choose wake time (now or custom)
 * - Step 3: Time picker for custom wake time
 * - Step indicator bars (like Behördenguide / Produktcheck)
 * - Safe-area support via bottom-sheet-footer CSS
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Moon, Sun, Loader2, X, Clock, ChevronRight, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useBabyCare } from '../index';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { getBabyName } from '@/lib/babyUtils';
import type { BabyCareEntry } from '@/services/babyCareService';

interface ActiveSleepTimerCardProps {
  /** Compact mode for dashboard display */
  compact?: boolean;
}

/**
 * Renders all active sleep timer cards (one per sleeping baby).
 */
export function ActiveSleepTimerCard({ compact = false }: ActiveSleepTimerCardProps) {
  const { activeSleepSessions, endSleepSession, cancelSleepSession, isLoading } = useBabyCare();
  const { lastCompletedPregnancy } = usePregnancyStore();

  if (isLoading || activeSleepSessions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {activeSleepSessions.map((session) => (
        <SingleSleepTimer
          key={session.id}
          session={session}
          compact={compact}
          babyName={getBabyName(lastCompletedPregnancy, session.baby_index)}
          onEndSleep={endSleepSession}
          onCancelSleep={cancelSleepSession}
        />
      ))}
    </div>
  );
}

type ModalStep = 'choice' | 'cancel-confirm' | 'end-options' | 'time-picker';

const STEP_INDEX: Record<ModalStep, number> = {
  choice: 0,
  'cancel-confirm': 1,
  'end-options': 1,
  'time-picker': 2,
};

/**
 * Single sleep timer card for one baby.
 */
interface SingleSleepTimerProps {
  session: BabyCareEntry;
  compact: boolean;
  babyName: string;
  onEndSleep: (id: string, endedAt?: string) => Promise<BabyCareEntry | null>;
  onCancelSleep: (id: string) => Promise<boolean>;
}

function SingleSleepTimer({ session, compact, babyName, onEndSleep, onCancelSleep }: SingleSleepTimerProps) {
  const { t } = useTranslation('babyCare');
  const [isEndingSleep, setIsEndingSleep] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('choice');
  const [selectedTime, setSelectedTime] = useState<string>('');

  const currentStep = STEP_INDEX[modalStep];

  // Update elapsed time every second
  useEffect(() => {
    const updateElapsedTime = () => {
      const startTime = new Date(session.recorded_at);
      const now = new Date();
      const diffMs = now.getTime() - startTime.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;

      if (hours > 0) {
        setElapsedTime(`${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
      } else {
        setElapsedTime(`${minutes}:${String(seconds).padStart(2, '0')}`);
      }
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);
    return () => clearInterval(interval);
  }, [session.recorded_at]);

  // Reset time picker when entering time-picker step
  useEffect(() => {
    if (modalStep === 'time-picker') {
      const now = new Date();
      setSelectedTime(now.toTimeString().slice(0, 5));
    }
  }, [modalStep]);

  const openModal = () => {
    setModalStep('choice');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleEndSleepNow = async () => {
    setIsEndingSleep(true);
    await onEndSleep(session.id);
    setIsEndingSleep(false);
    closeModal();
  };

  const handleEndSleepWithTime = async () => {
    if (!selectedTime) return;

    const today = new Date();
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);

    const startTime = new Date(session.recorded_at);
    const now = new Date();

    if (endTime > now) {
      endTime.setDate(endTime.getDate() - 1);
    }

    if (endTime < startTime) {
      alert(t('entryModal.invalidSleepTime'));
      return;
    }

    setIsEndingSleep(true);
    await onEndSleep(session.id, endTime.toISOString());
    setIsEndingSleep(false);
    closeModal();
  };

  const handleCancelSleep = async () => {
    setIsCancelling(true);
    await onCancelSleep(session.id);
    setIsCancelling(false);
    closeModal();
  };

  const startTimeFormatted = new Date(session.recorded_at).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Step indicator (2 bars for 3 logical steps)
  const stepIndicator = (
    <div className="flex gap-1 px-4 pb-3">
      {Array.from({ length: 2 }, (_, i) => (
        <div
          key={i}
          className={`flex-1 h-1 rounded-full ${
            i < currentStep ? 'bg-pp-orange' : 'bg-white/20'
          }`}
        />
      ))}
    </div>
  );

  return (
    <>
      {/* Sleep Timer Card — glass effect with oversized decorative Moon */}
      <div className="glass-effect rounded-[15px] p-4 relative overflow-hidden">
        {/* Oversized decorative Moon icon (~150% of card height, clipped) */}
        <div className="absolute -left-6 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-[140px] h-[140px] rounded-full bg-white/10 flex items-center justify-center">
            <Moon className="w-16 h-16 text-pp-dark/25" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              {elapsedTime && (
                <span className="font-mono text-2xl text-white font-semibold">{elapsedTime}</span>
              )}
              <p className="text-white/70 text-sm mt-0.5">
                {babyName} {t('sleepTimer.sleeping')}
                {!compact && (
                  <span> · {t('sleepTimer.since', { time: startTimeFormatted })}</span>
                )}
              </p>
            </div>
            <button
              onClick={openModal}
              className="px-5 py-2.5 bg-pp-orange text-white rounded-[10px] font-medium hover:bg-pp-orange/90 transition-colors flex-shrink-0"
            >
              {t('sleepTimer.end')}
            </button>
          </div>
        </div>
      </div>

      {/* Multi-step Bottom Sheet Modal */}
      {showModal && createPortal(
        <>
          <div
            className="bottom-sheet-backdrop"
            onClick={closeModal}
          />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">

              {/* ── Step 1: Choice (Abbruch / Aufgewacht) ── */}
              {modalStep === 'choice' && (
                <>
                  <div className="bottom-sheet-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                        <Moon className="w-5 h-5 text-indigo-400" />
                      </div>
                      <h2>{t('sleepTimer.whatToDo')}</h2>
                    </div>
                    <button
                      onClick={closeModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-3">
                    {/* Woke up option */}
                    <button
                      onClick={() => setModalStep('end-options')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Sun className="w-5 h-5 text-pp-orange" />
                        <div className="text-left">
                          <span className="text-white font-medium block">{t('sleepTimer.wokeUp')}</span>
                          <span className="text-white/50 text-sm">{t('sleepTimer.wokeUpDesc')}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                    </button>

                    {/* Cancel option */}
                    <button
                      onClick={() => setModalStep('cancel-confirm')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <X className="w-5 h-5 text-red-400" />
                        <div className="text-left">
                          <span className="text-white font-medium block">{t('sleepTimer.cancel')}</span>
                          <span className="text-white/50 text-sm">{t('sleepTimer.cancelDesc')}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                    </button>
                  </div>

                  <div className="bottom-sheet-footer" />
                </>
              )}

              {/* ── Step 2a: Cancel Confirmation ── */}
              {modalStep === 'cancel-confirm' && (
                <>
                  <div className="bottom-sheet-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      </div>
                      <h2>{t('sleepTimer.cancelConfirmTitle')}</h2>
                    </div>
                    <button
                      onClick={closeModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content">
                    <p className="text-white/70 text-center">
                      {t('sleepTimer.cancelConfirmDescription', { name: babyName })}
                    </p>
                    <div className="mt-4 p-3 glass-effect rounded-[10px] text-center">
                      <p className="text-white/60 text-sm">{t('sleepTimer.sleepStarted')}</p>
                      <p className="text-white font-medium">{startTimeFormatted} {t('sleepTimer.timeUnit')}</p>
                      <p className="text-white/60 text-sm mt-1">{t('sleepTimer.durationLabel', { duration: elapsedTime })}</p>
                    </div>
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setModalStep('choice')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {t('sleepTimer.back')}
                    </button>
                    <button
                      onClick={handleCancelSleep}
                      disabled={isCancelling}
                      className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      {isCancelling ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        t('sleepTimer.deleteEntry')
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 2b: End Options (Jetzt / Zeit wählen) ── */}
              {modalStep === 'end-options' && (
                <>
                  <div className="bottom-sheet-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                        <Sun className="w-5 h-5 text-pp-orange" />
                      </div>
                      <h2>{t('sleepTimer.wakeQuestion', { name: babyName })}</h2>
                    </div>
                    <button
                      onClick={closeModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-3">
                    {/* Now Option */}
                    <button
                      onClick={handleEndSleepNow}
                      disabled={isEndingSleep}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-pp-orange" />
                        <span className="text-white font-medium">{t('sleepTimer.now')}</span>
                      </div>
                      {isEndingSleep ? (
                        <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-white/60" />
                      )}
                    </button>

                    {/* Custom Time Option */}
                    <button
                      onClick={() => setModalStep('time-picker')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-indigo-400" />
                        <span className="text-white font-medium">{t('sleepTimer.chooseTime')}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60" />
                    </button>
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setModalStep('choice')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {t('sleepTimer.back')}
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 3: Time Picker ── */}
              {modalStep === 'time-picker' && (
                <>
                  <div className="bottom-sheet-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-indigo-400" />
                      </div>
                      <h2>{t('sleepTimer.chooseWakeTime')}</h2>
                    </div>
                    <button
                      onClick={closeModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-4">
                    <p className="text-white/60 text-sm text-center">
                      {t('sleepTimer.sleptSince', { name: babyName, time: startTimeFormatted })}
                    </p>
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full p-4 bg-white/10 rounded-[15px] text-white text-center text-2xl font-mono border border-white/20 focus:border-pp-orange focus:outline-none"
                    />
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setModalStep('end-options')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {t('sleepTimer.back')}
                    </button>
                    <button
                      onClick={handleEndSleepWithTime}
                      disabled={isEndingSleep || !selectedTime}
                      className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                    >
                      {isEndingSleep ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        t('sleepTimer.confirm')
                      )}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
